import { DateHelper, IDateTimeType } from '@connect-soft/date-helper';
import Box from '@mui/material/Box';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import { styled } from '@mui/material/styles';
import { ITimePickerProps, TimePicker } from '../TimePicker';

type IValueType<T> = {
    start: T;
    end: T;
};
export type ITimeRangePickerValueType = {
    start: ITimePickerProps['value'] | null;
    duration: number | null;
};

export type ITimeRangePickerProps = Omit<
    ITimePickerProps,
    'label' | 'minTime' | 'maxTime' | 'error' | 'helperText' | 'onError' | 'onChange' | 'value'
> & {
    label?: string | IValueType<string>;
    value?: ITimeRangePickerValueType;
    minTime?: ITimePickerProps['value'];
    maxTime?: ITimePickerProps['value'];
    error?: boolean | IValueType<boolean>;
    helperText?: string | IValueType<string>;
    zeroIsNextDay?: boolean;
    onError?: (message: IValueType<string>) => void;
    onChange?: (value: ITimeRangePickerValueType) => void;
};

const StyledWrapper = styled(Box)(({ hidden }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    ...(hidden && { display: 'none' })
}));
const StyledInput = styled(TimePicker)`
    flex: 1;
`;
const StyledSign = styled(Box, {
    shouldForwardProp: (propName) => 'size' !== propName
})<{
    size: ITimeRangePickerProps['size'];
}>(({ theme, hidden, size }) => ({
    margin: `${theme.spacing(1)} ${theme.spacing(1.7)} 0`,
    ...(size === 'small' && { fontSize: '1rem' }),
    ...(hidden && { display: 'none' })
}));

export const TimeRangePicker = ({
    error = false,
    helperText,
    label,
    maxTime,
    minTime,
    name,
    required,
    value,
    zeroIsNextDay = false,
    onError,
    onChange,
    ...rest
}: ITimeRangePickerProps) => {
    const getEndTime = (start: Date | IDateTimeType | null = null, duration: number | null = null) =>
        DateHelper.from(start).addHours(duration ?? 0, true);
    const isNextDay = (start: Date | IDateTimeType | null, duration: number | null = null) => {
        const midnight = DateHelper.from(start).addDays(1).getStartOfDay(true);
        const end = getEndTime(start, duration);

        return end.isAfter(midnight) || end.isEqual(midnight);
    };

    const currentEndTime = getEndTime(value?.start, value?.duration);

    return (
        <>
            {typeof label === 'string' && <FormLabel required={required}>{label}</FormLabel>}
            <StyledWrapper>
                <StyledInput
                    {...rest}
                    name={`${name}.start`}
                    value={value?.start ?? null}
                    minTime={minTime ?? undefined}
                    maxTime={currentEndTime?.getObj() ?? maxTime ?? undefined}
                    error={typeof error === 'boolean' ? error : !!error?.start}
                    helperText={typeof helperText === 'object' ? helperText?.start : undefined}
                    onError={(message) => onError && onError({ start: message, end: '' })}
                    onChange={
                        onChange
                            ? (start) => {
                                  if (value?.start) {
                                      const valueStart = DateHelper.from(value.start);
                                      const diff = valueStart.diff(start, 'hours', true);

                                      onChange({
                                          start,
                                          duration:
                                              typeof value?.duration === 'number'
                                                  ? Math.round(value.duration - diff)
                                                  : value?.duration
                                      });
                                  } else {
                                      onChange({
                                          duration: value?.duration ?? null,
                                          start
                                      });
                                  }
                              }
                            : undefined
                    }
                />
                <StyledSign size={rest.size}> - </StyledSign>
                <StyledInput
                    {...rest}
                    name={`${name}.end`}
                    value={currentEndTime?.getObj()}
                    minTime={value?.start ?? minTime ?? undefined}
                    maxTime={maxTime ?? undefined}
                    error={typeof error === 'boolean' ? error : !!error?.end}
                    helperText={
                        typeof helperText === 'object'
                            ? helperText?.end
                                ? helperText.end
                                : isNextDay(value?.start ?? null, value?.duration ?? null)
                                  ? 'next day'
                                  : helperText?.end
                            : undefined
                    }
                    onError={(message) => onError && onError({ start: '', end: message })}
                    onChange={
                        onChange
                            ? (end) => {
                                  const newEnd = DateHelper.from(end);

                                  if (value?.start) {
                                      const start = DateHelper.from(value.start);
                                      const diff = start.diff(newEnd, 'hours', true);

                                      onChange({
                                          ...value,
                                          duration: Math.round(diff)
                                      });
                                  } else {
                                      onChange({
                                          start: end,
                                          duration: 0
                                      });
                                  }
                              }
                            : undefined
                    }
                />
            </StyledWrapper>
            {typeof helperText === 'string' && (
                <FormHelperText error={typeof error === 'boolean' && error}>{helperText}</FormHelperText>
            )}
        </>
    );
};
